import { HttpClient } from '@angular/common/http';
import { INJECTOR, Injector, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, createSelector } from '@ngrx/store';
import {
  EMPTY,
  catchError,
  combineLatest,
  debounceTime,
  delayWhen,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  from,
  map,
  merge,
  mergeMap,
  of,
  pairwise,
  startWith,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import { conformsTo, isArrayAndEveryElement, isBoolean, isDefined, isNullable, isNumber, isString } from '@dmb/core';
import { WaveFormatValidator } from '@knm/wave-validator';

import { logError } from '../../../helper/log.helper';
import { Explicitness, KIPolicies, NewTrack, isExplicitness, isKIPolicy } from '../../../model';
import { CoverImage, CoverType, Genre } from '../../../model/product/general';
import { PopupCenterService } from '../../../popup-center/popup-center.service';
import { SessionService } from '../../../services';
import { skipHttpErrorPage } from '../../../services/http-error/http-error.interceptor';
import { DBService } from '../../../services/indexed-db';
import {
  isJpeg,
  isValidISRC,
  noEmoji,
  validAspectRatio,
  validEanUpc,
  validSize,
  validTitle,
  validTitleVersion,
} from '../../../validators/validators';
import {
  MAX_SIZE_SINGLE_PART_UPLOAD,
  cancelUpload,
  interruptAll,
  multiPartUpload,
  singlePartUpload,
} from '../uploadUtils';
import { parsedGenreId } from '../utils';
import {
  AsyncValidationActions,
  UploadActions,
  WizardActions,
  WizardSessionActions,
  setValueActionTypes,
} from './app.actions';
import { WizardStore, wizardFeature } from './app.reducer';
import {
  constraintSelectors,
  isSpokenWord,
  selectDefinedBisacGenres,
  selectDefinedGenres,
  selectDefinedWgsGenres,
  trackToAnalyze,
  valueSelectors,
} from './app.selector';

export const addTimestamps = createEffect(
  () => {
    const actions$ = inject(Actions);
    const store = inject<WizardStore>(Store);

    const valueUpdateActions = actions$.pipe(ofType(...Object.values(WizardActions), ...setValueActionTypes));
    const uploadActions = actions$.pipe(
      ofType(UploadActions.done, UploadActions.multiCreated, UploadActions.partDone, UploadActions.allPartsDone),
    );

    return store.select(valueSelectors.id).pipe(
      switchMap((currentSessionId) =>
        merge(valueUpdateActions, uploadActions.pipe(filter(({ sessionId }) => sessionId === currentSessionId))).pipe(
          debounceTime(1000),
        ),
      ),
      map(() => WizardSessionActions.newTimestamp()),
    );
  },
  { functional: true },
);

export const saveToDbEffect = createEffect(
  () => {
    const actions$ = inject(Actions);
    const db = inject(DBService);
    const currentWizardState = inject<WizardStore>(Store).selectSignal(wizardFeature.selectValue);

    return actions$.pipe(
      ofType(WizardSessionActions.newTimestamp),
      tap(() => {
        if (db.status() !== 'resolved') {
          return;
        }

        db.putProductWizardSession(currentWizardState());
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const loadFromDbEffect = createEffect(
  () => {
    const actions$ = inject(Actions);
    const db = inject(DBService);
    const popupCenter = inject(PopupCenterService);

    return actions$.pipe(
      ofType(WizardSessionActions.sessionSelected),
      map(({ id }) => {
        if (db.status() !== 'resolved') {
          console.assert(false, 'Should not happen, because session list cannot be loaded until db is resolved');
          return WizardSessionActions.sessionLoadFailed({ reason: 'db not ready' });
        }

        try {
          return WizardSessionActions.sessionLoaded({ value: db.loadProductWizardSession(id) });
        } catch (reason) {
          const err = new Error('Load from DB failed', { cause: reason });
          logError(err);

          popupCenter.addPopup({
            title: $localize`:@@product-wizard.session.load-failed:Loading session failed`,
            timeout: 5000,
            type: 'error',
          });
          return WizardSessionActions.sessionLoadFailed({ reason });
        }
      }),
    );
  },
  { functional: true },
);

export const deleteFromDbEffect = createEffect(
  () => {
    const actions$ = inject(Actions);
    const db = inject(DBService);

    return actions$.pipe(
      ofType(WizardSessionActions.deleteSessionStarted),
      switchMap(async ({ id, fileIds }) => {
        try {
          await db.deleteProductWizardSession(id, fileIds);
          return WizardSessionActions.deleteSessionDone({ id });
        } catch (reason) {
          const err = new Error('Delete from DB failed', { cause: reason });
          logError(err);

          return WizardSessionActions.deleteSessionDone({ id });
        }
      }),
    );
  },
  { functional: true },
);

const eanUpcValidationDependencies = createSelector({
  code: valueSelectors.productData.eanUpc,
  generated: valueSelectors.eanWasGenerated,
});
export const validateEanUpcEffect = createEffect(
  () => {
    const store = inject<WizardStore>(Store);
    const http = inject(HttpClient);

    return store.select(eanUpcValidationDependencies).pipe(
      debounceTime(500),
      filter(
        ({ code, generated }) => !generated && (code.length === 12 || code.length === 13) && validEanUpc(code) === null,
      ),
      switchMap(({ code }) => {
        const code_type = code.length === 12 ? 'UPC' : 'EAN';

        return http
          .post(
            '/api/v5/product/product-codes/validate',
            { code_type, product_type: 'ALBUM', code },
            { context: skipHttpErrorPage() },
          )
          .pipe(
            map((result) => {
              if (!conformsTo({ is_valid: isBoolean, error_msg: isNullable(isString) })(result)) {
                throw new Error('Received invalid result from product code validation', { cause: result });
              }

              return AsyncValidationActions.finishedValidatingProductcode({
                result: result.is_valid ? null : (result.error_msg ?? null),
              });
            }),
            catchError((err) => {
              if (err !== null && typeof err === 'object' && 'error' in err) {
                logError(err.error);
              } else if (err instanceof Error && err.cause) {
                logError(err, { extra: { cause: err.cause } });
              } else {
                logError(err);
              }

              return of(AsyncValidationActions.finishedValidatingProductcode({ result: null }));
            }),
            startWith(AsyncValidationActions.startedValidatingProductcode()),
          );
      }),
    );
  },
  { functional: true },
);

function validateTitle(
  injector: Injector,
  target: { validating: 'TITLE' | 'TITLE_VERSION' } & (
    | { on: 'ALBUM'; trackId?: undefined }
    | { on: 'TRACK'; trackId: string }
  ),
  {
    product_title,
    language_code,
    genres,
    bisacGenre,
    wgsGenre,
  }: { product_title: string; language_code: string; genres: Genre[]; bisacGenre: Genre[]; wgsGenre: Genre[] },
) {
  if (product_title === '') {
    return of(
      AsyncValidationActions.finishedValidatingTitle({ ...target, warnings: null, errors: null, explicit: false }),
    );
  }

  const genreIdList = genres.map((g) => g.id);
  const bisacGenreIdList = bisacGenre.map((g) => g.id);
  const wgsGenreIdList = wgsGenre.map((g) => g.id);
  let genre_ids: string[] | undefined = genreIdList.length > 0 ? genreIdList : [];

  if (bisacGenreIdList.length > 0) {
    genre_ids = [...genre_ids, ...bisacGenreIdList];
  }

  if (wgsGenreIdList.length > 0) {
    genre_ids = [...genre_ids, ...wgsGenreIdList];
  }

  if (genre_ids.length <= 0) {
    genre_ids = undefined;
  }

  const property_name =
    target.validating === 'TITLE_VERSION'
      ? $localize`:@@product-wizard.data.title-version:Title Version`
      : $localize`:@@product-wizard.data.title:Title`;

  return injector
    .get(HttpClient)
    .post(
      '/api/v5/product/products/validate-title',
      {
        product_type: target.on,
        product_title,
        language_code,
        genre_ids,
        property_name,
      },
      { context: skipHttpErrorPage() },
    )
    .pipe(
      map((result) => {
        if (
          !conformsTo({
            warnings: isNullable(isArrayAndEveryElement(isString)),
            errors: isNullable(isArrayAndEveryElement(isString)),
            actions: isNullable(isArrayAndEveryElement(isString)),
          })(result)
        ) {
          throw new Error('Received invalid result from product title validation', { cause: result });
        }

        const errors = result.errors && result.errors.length > 0 ? result.errors : null;
        const warnings = result.warnings && result.warnings.length > 0 ? result.warnings : null;
        return AsyncValidationActions.finishedValidatingTitle({
          ...target,
          errors,
          warnings,
          explicit: result.actions?.includes('set-explicit') ?? false,
        });
      }),
      catchError((err) => {
        logError(err);

        return of(
          AsyncValidationActions.finishedValidatingTitle({ ...target, warnings: null, errors: null, explicit: false }),
        );
      }),
      startWith(AsyncValidationActions.startedValidatingTitle(target)),
    );
}

const productTitleValidationDependencies = createSelector({
  product_title: valueSelectors.productData.title,
  language_code: valueSelectors.productData.language,
  genres: selectDefinedGenres,
  bisacGenre: selectDefinedBisacGenres,
  wgsGenre: selectDefinedWgsGenres,
});
export const validateProductTitleEffect = createEffect(
  () => {
    const store = inject<WizardStore>(Store);
    const injector = inject(INJECTOR);

    return store.select(productTitleValidationDependencies).pipe(
      debounceTime(500),
      filter(
        (values) =>
          noEmoji(values.product_title) === null && validTitle(values.product_title, values.language_code) === null,
      ),
      switchMap((values) => validateTitle(injector, { on: 'ALBUM', validating: 'TITLE' }, values)),
    );
  },
  { functional: true },
);

const productTitleVersionValidationDependencies = createSelector({
  product_title: valueSelectors.productData.titleVersion,
  language_code: valueSelectors.productData.language,
  genres: selectDefinedGenres,
  bisacGenre: selectDefinedBisacGenres,
  wgsGenre: selectDefinedWgsGenres,
});
export const validateProductTitleVersionEffect = createEffect(
  () => {
    const store = inject<WizardStore>(Store);
    const injector = inject(INJECTOR);

    return store.select(productTitleVersionValidationDependencies).pipe(
      debounceTime(500),
      filter((values) => noEmoji(values.product_title) === null && validTitleVersion(values.product_title) === null),
      switchMap((values) => validateTitle(injector, { on: 'ALBUM', validating: 'TITLE_VERSION' }, values)),
    );
  },
  { functional: true },
);

function validateTrackTitle(injector: Injector, trackId: string, validating: 'TITLE' | 'TITLE_VERSION') {
  return injector
    .get<WizardStore>(Store)
    .select(valueSelectors.tracklisting)
    .pipe(
      map((allTracks) => allTracks.find((t): t is NewTrack => t.id === trackId && !t.existing)),
      filter(isDefined),
      map(
        validating === 'TITLE'
          ? (track) => ({
              product_title: track.title,
              language_code: track.metadata.titleLanguage,
              genres: track.metadata.genre.filter(isDefined),
              bisacGenre: track.metadata.bisacGenre.filter(isDefined),
              wgsGenre: track.metadata.wgsGenre.filter(isDefined),
            })
          : (track) => ({
              product_title: track.titleVersion,
              language_code: track.metadata.titleLanguage,
              genres: track.metadata.genre.filter(isDefined),
              bisacGenre: track.metadata.bisacGenre.filter(isDefined),
              wgsGenre: track.metadata.wgsGenre.filter(isDefined),
            }),
      ),
      distinctUntilChanged(
        (previous, current) =>
          previous.product_title === current.product_title &&
          previous.language_code === current.language_code &&
          previous.genres.length === current.genres.length &&
          previous.genres.every((g, index) => current.genres[index]?.id === g.id) &&
          previous.bisacGenre.length === current.bisacGenre.length &&
          previous.bisacGenre.every((g, index) => current.bisacGenre[index]?.id === g.id) &&
          previous.wgsGenre.length === current.wgsGenre.length &&
          previous.wgsGenre.every((g, index) => current.wgsGenre[index]?.id === g.id),
      ),
      debounceTime(500),
      filter(
        validating === 'TITLE'
          ? ({ product_title, language_code }) =>
              noEmoji(product_title) === null && validTitle(product_title, language_code) === null
          : ({ product_title }) => noEmoji(product_title) === null && validTitleVersion(product_title) === null,
      ),
      switchMap((values) => validateTitle(injector, { on: 'TRACK', validating, trackId }, values)),
    );
}

function validateTrackIsrc(injector: Injector, trackId: string) {
  return injector
    .get<WizardStore>(Store)
    .select(valueSelectors.tracklisting)
    .pipe(
      map((allTracks) => allTracks.find((t): t is NewTrack => t.id === trackId && !t.existing)),
      filter(isDefined),
      map((track) => ({ code: track.isrc, generated: track.isrcWasGenerated })),
      distinctUntilChanged(
        (previous, current) => previous.code === current.code && previous.generated === current.generated,
      ),
      debounceTime(500),
      filter(({ code, generated }) => !generated && code !== '' && isValidISRC(code)),
      switchMap(({ code }) =>
        injector
          .get(HttpClient)
          .post(
            '/api/v5/product/product-codes/validate',
            { code_type: 'ISRC', product_type: 'TRACK', code },
            { context: skipHttpErrorPage() },
          )
          .pipe(
            map((result) => {
              if (!conformsTo({ is_valid: isBoolean, error_msg: isNullable(isString) })(result)) {
                throw new Error('Received invalid result from product code validation', { cause: result });
              }

              return AsyncValidationActions.finishedValidatingTrackISRC({
                trackId,
                error: result.is_valid || !result.error_msg ? null : result.error_msg,
              });
            }),
            catchError((err) => {
              logError(err);

              return of(AsyncValidationActions.finishedValidatingTrackISRC({ trackId, error: null }));
            }),
            startWith(AsyncValidationActions.startedValidatingTrackISRC({ trackId })),
          ),
      ),
    );
}

export const validateTrackValuesEffect = createEffect(
  () => {
    const injector = inject(INJECTOR);

    const newTrackIds$ = inject<WizardStore>(Store)
      .select(valueSelectors.tracklisting)
      .pipe(
        map((tracks) => {
          const ids: string[] = [];

          tracks.forEach((t) => {
            if (!t.existing) {
              ids.push(t.id);
            }
          });

          return ids;
        }),
      );

    return newTrackIds$.pipe(
      startWith([] as string[]),
      pairwise(),
      // Only start validations for newly added tracks, already running ones keep going.
      mergeMap(([previous, current]) => current.filter((id) => !previous.includes(id))),
      mergeMap((trackId) =>
        merge(
          validateTrackTitle(injector, trackId, 'TITLE'),
          validateTrackTitle(injector, trackId, 'TITLE_VERSION'),
          validateTrackIsrc(injector, trackId),
        ).pipe(
          // Stop once the track is gone (deleted or session switched), incl. pending debounce and requests.
          takeUntil(newTrackIds$.pipe(filter((ids) => !ids.includes(trackId)))),
        ),
      ),
    );
  },
  { functional: true },
);

// Using a simple cache, because files can not change; we won't have to revalidate
const trackValidations = new Map<string, Promise<string[] | null>>();
const trackValidationResults = new Map<string, string[] | null>();
const waveValidator = WaveFormatValidator.newDefaultStereoValidator();

export const validateTracksEffect = createEffect(
  () => {
    const store = inject<WizardStore>(Store);
    const asyncValidations = store.selectSignal(wizardFeature.selectAsyncValidations);

    return store.select(valueSelectors.tracklisting).pipe(
      mergeMap((tracks) =>
        tracks.map((track) => {
          if (track.existing) {
            return EMPTY;
          }

          const { file, id } = track;
          const general = asyncValidations().tracks[id]?.general;

          if (trackValidationResults.has(id)) {
            const errors = trackValidationResults.get(id) ?? null;

            // Only restore the result if the state lost it, e.g. after loading a session
            if (general && !general.pending && general.errors === errors) {
              return EMPTY;
            }

            return of(AsyncValidationActions.finishedValidatingTrack({ trackId: id, errors }));
          }

          if (trackValidations.has(id)) {
            // Still running, its result will be dispatched once done
            return general?.pending ? EMPTY : of(AsyncValidationActions.startedValidatingTrack({ trackId: id }));
          }

          if (file === undefined) {
            return EMPTY;
          }

          const validation = waveValidator.validateFile(file).then((result) => {
            const errors = result.length > 0 ? result : null;
            trackValidationResults.set(id, errors);
            return errors;
          });
          trackValidations.set(id, validation);

          return from(validation).pipe(
            map((errors) => AsyncValidationActions.finishedValidatingTrack({ trackId: id, errors })),
            startWith(AsyncValidationActions.startedValidatingTrack({ trackId: id })),
          );
        }),
      ),
      mergeMap((obs) => obs),
    );
  },
  { functional: true },
);

export const validatePreviewFile = createEffect(
  () => {
    return inject<WizardStore>(Store)
      .select(valueSelectors.tracksPreview)
      .pipe(
        map((obj) => obj?.file),
        distinctUntilChanged(),
        switchMap((file) => {
          if (file === undefined) {
            return of(AsyncValidationActions.finishedValidatingPreviewFile({ errors: null }));
          }

          return from(waveValidator.validateFile(file)).pipe(
            map((result) => {
              const errors = result.length > 0 ? result : null;

              return AsyncValidationActions.finishedValidatingPreviewFile({
                errors,
              });
            }),
            startWith(AsyncValidationActions.startedValidatingPreviewFile()),
          );
        }),
      );
  },
  { functional: true },
);

function canBeValidated(image: CoverImage): image is Omit<CoverImage, 'file'> & { file: File } {
  if (image.file === undefined) {
    return false;
  }
  return (image.type === CoverType.FRONT || image.type === CoverType.BACK) && isJpeg(image) === null;
}

const singleTrack = createSelector(valueSelectors.tracklisting, (tracks) => tracks.length === 1);
const coverDimensionValidationDependencies = createSelector({
  images: valueSelectors.productData.coverImages,
  productType: valueSelectors.productType,
  singleTrack,
  minWidth: constraintSelectors.minCoverWidth,
  minHeight: constraintSelectors.minCoverHeight,
});
export const validateCoverDimensionsEffect = createEffect(
  () => {
    return inject<WizardStore>(Store)
      .select(coverDimensionValidationDependencies)
      .pipe(
        map(({ images, ...rest }) => ({ ...rest, images: images.filter(canBeValidated) })),
        filter(({ images }) => images.length > 0),
        switchMap(({ images, productType, singleTrack, minWidth, minHeight }) => {
          const imagePromises = images.map(({ file, id }) =>
            createImageBitmap(file)
              .then((bitmap) => {
                const sizeError = validSize(minHeight, minWidth, bitmap);
                const aspectRatioError = validAspectRatio(productType, singleTrack, bitmap);

                bitmap.close();

                if (sizeError === null && aspectRatioError === null) {
                  return { id, errors: null };
                }

                return { id, errors: [sizeError, aspectRatioError].filter((val) => val !== null) };
              })
              .catch((err) => {
                logError(err);
                return { id, errors: [$localize`:@@product-wizard.data.cover-cannot-decode:Unable to decode image`] };
              }),
          );

          return from(Promise.all(imagePromises)).pipe(
            map((allErrors) => AsyncValidationActions.finishedValidatingCovers({ allErrors })),
            catchError((err) => {
              logError(err);

              return of(AsyncValidationActions.finishedValidatingCovers({ allErrors: [] }));
            }),
            startWith(AsyncValidationActions.startedValidatingCovers()),
          );
        }),
      );
  },
  { functional: true },
);

const genre1_id = createSelector(valueSelectors.productData.genre, (genres) => parsedGenreId(genres[0]));
const genre2_id = createSelector(
  isSpokenWord,
  valueSelectors.productData.genre,
  valueSelectors.productData.bisacGenre,
  (spokenWord, genre, bisacGenre) => (spokenWord ? parsedGenreId(bisacGenre[0]) : parsedGenreId(genre[1])),
);
const genre3_id = createSelector(
  isSpokenWord,
  valueSelectors.productData.genre,
  valueSelectors.productData.wgsGenre,
  (spokenWord, genre, wgsGenre) => (spokenWord ? parsedGenreId(wgsGenre[0]) : parsedGenreId(genre[2])),
);
const constraintDependencies = createSelector({
  productType: valueSelectors.productType,
  genre1_id,
  genre2_id,
  genre3_id,
});

export const loadConstraintsEffect = createEffect(
  () => {
    const http = inject(HttpClient);
    const store = inject<WizardStore>(Store);

    return inject(SessionService).session.pipe(
      map((ses) => String(ses.selectedClientId)),
      distinctUntilChanged(),
      switchMap((clientId) => store.select(constraintDependencies).pipe(map((values) => ({ ...values, clientId })))),
      switchMap(({ productType, clientId, genre1_id, genre2_id, genre3_id }) => {
        if (productType === undefined) {
          return of(WizardSessionActions.canceledLoadingConstraints());
        }

        const albumConstraintsBody = {
          album_type: productType,
          genre1_id,
          genre2_id,
          genre3_id,
          has_booklet: true,
        };
        const trackSearchParams = new URLSearchParams({
          track_type: 'AUDIO', // May have to support VIDEO in the future
          track_sub_type: ['SINGLE', 'EP', 'ALBUM', 'COMPILATION', 'VIDEO_BUNDLE'].includes(productType)
            ? 'MUSIC'
            : productType,
        });

        return combineLatest([
          http.post('/api/v5/audio/albums/constraints', albumConstraintsBody, { context: skipHttpErrorPage() }),
          http.get('/api/v5/audio/tracks/constraints?' + trackSearchParams.toString(), {
            context: skipHttpErrorPage(),
          }),
        ]).pipe(
          map(([albumResult, trackResult]) => {
            if (
              !conformsTo({
                metadata_constraints: conformsTo({
                  allow_ean_generation: isBoolean,
                  script_languages: isArrayAndEveryElement(conformsTo({ locale: isString, label: isString })),
                  audio_languages: isArrayAndEveryElement(conformsTo({ locale: isString, label: isString })),
                  contributor_roles: isArrayAndEveryElement(
                    conformsTo({ id: isNumber, name_localized: isString, is_major_role: isBoolean }),
                  ),
                  contributor_role_ids_preselected: isArrayAndEveryElement(isNumber),
                  contributor_role_ids_with_store_id: isArrayAndEveryElement(isNumber),
                  price_codes: isArrayAndEveryElement(
                    conformsTo({
                      code: isString,
                      consumer_price_max: isNullable(isNumber),
                      consumer_price_min: isNullable(isNumber),
                      name: isString,
                    }),
                  ),
                  price_codes_itunes: isArrayAndEveryElement(
                    conformsTo({
                      code: isString,
                      dealer_price: isNullable(isNumber),
                      name: isString,
                    }),
                  ),
                  age_rating_required: isBoolean,
                  booklet_cline_required: isBoolean,
                }),
                release_constraints: conformsTo({
                  allow_neighbouring_rights: isBoolean,
                  allow_third_party_accounting: isBoolean,
                  available_country_ids: isArrayAndEveryElement(isNumber),
                }),
                tracks_constraints: conformsTo({
                  min_track_count: isNullable(isNumber),
                }),
                front_cover_constraints: conformsTo({
                  min_width: isNullable(isNumber),
                  min_height: isNullable(isNumber),
                }),
              })(albumResult)
            ) {
              throw new Error('Received invalid result from album constraints request', { cause: albumResult });
            }

            if (
              !conformsTo({
                metadata_constraints: conformsTo({
                  allow_isrc_generation: isBoolean,
                }),
              })(trackResult)
            ) {
              throw new Error('Received invalid result from track constraints request', { cause: trackResult });
            }

            const constraints = {
              canGenerateEAN: albumResult.metadata_constraints.allow_ean_generation,
              scriptLanguages: albumResult.metadata_constraints.script_languages.map((l) => ({
                label: l.label,
                value: l.locale,
              })),
              audioLanguages: albumResult.metadata_constraints.audio_languages.map((l) => ({
                label: l.label,
                value: l.locale,
              })),
              roles: albumResult.metadata_constraints.contributor_roles
                .sort((a, b) => {
                  if (a.is_major_role && !b.is_major_role) {
                    return -1;
                  }

                  if (!a.is_major_role && b.is_major_role) {
                    return 1;
                  }

                  return a.name_localized.localeCompare(b.name_localized);
                })
                .map((c) => ({
                  name: c.name_localized,
                  id: String(c.id),
                  isMajor: c.is_major_role,
                })),
              rolesRequiringStoreIds: albumResult.metadata_constraints.contributor_role_ids_with_store_id.map(String),
              preselectedRoles: albumResult.metadata_constraints.contributor_role_ids_preselected.map(String),
              priceCodes: albumResult.metadata_constraints.price_codes.filter(
                (p): p is typeof p & { consumer_price_min: number; consumer_price_max: number } =>
                  typeof p.consumer_price_min === 'number' && typeof p.consumer_price_max === 'number',
              ),
              itunesPriceCodes: albumResult.metadata_constraints.price_codes_itunes
                .filter((p): p is typeof p & { dealer_price: number } => typeof p.dealer_price === 'number')
                .sort((a, b) => a.dealer_price - b.dealer_price),
              ageRatingRequired: albumResult.metadata_constraints.age_rating_required,
              bookletCLineRequired: albumResult.metadata_constraints.booklet_cline_required,
              canHaveThirdPartyAccounting: albumResult.release_constraints.allow_third_party_accounting,
              canHaveNeighbouringRights: albumResult.release_constraints.allow_neighbouring_rights,
              allowedCountries: albumResult.release_constraints.available_country_ids.map((id) => String(id)),
              minTrackCount: albumResult.tracks_constraints.min_track_count ?? undefined,
              minCoverWidth: albumResult.front_cover_constraints.min_width ?? undefined,
              minCoverHeight: albumResult.front_cover_constraints.min_height ?? undefined,
              canGenerateIsrc: trackResult.metadata_constraints.allow_isrc_generation,
              clientId,
            };

            return WizardSessionActions.finishedLoadingConstraints(constraints);
          }),
          catchError((err) => {
            if (err !== null && typeof err === 'object' && 'error' in err) {
              logError(err.error);
            } else if (err instanceof Error && err.cause) {
              logError(err, { extra: { cause: err.cause } });
            } else {
              logError(err);
            }

            return of(WizardSessionActions.failedLoadingConstraints());
          }),
          startWith(WizardSessionActions.startedLoadingConstraints()),
        );
      }),
    );
  },
  { functional: true },
);

export const restartFileUpload = createEffect(
  () => {
    return inject(Actions).pipe(
      ofType(WizardSessionActions.sessionLoaded),
      map(({ value }) => {
        const imagesToRestart = value.productData.coverImages.filter((image) => image.uploadInfo.status !== 'done');
        const tracksToRestart = value.tracklisting.filter(
          (track): track is NewTrack => !track.existing && track.uploadInfo.status !== 'done',
        );
        const trackPreview =
          value.tracksPreview && value.tracksPreview.uploadInfo.status !== 'done' ? value.tracksPreview : undefined;

        return { sessionId: value.id, imagesToRestart, tracksToRestart, trackPreview };
      }),
      filter(
        ({ imagesToRestart, tracksToRestart, trackPreview }) =>
          imagesToRestart.length > 0 || tracksToRestart.length > 0 || trackPreview !== undefined,
      ),
      map(({ sessionId, imagesToRestart, tracksToRestart, trackPreview }) => {
        const uploads: {
          id: string;
          file: File;
          finishedParts?: { partNumber: number; etag?: string; checksum?: string }[];
          uploadId?: string;
        }[] = [];

        imagesToRestart.forEach(({ uploadInfo, id, file }) => {
          if (file === undefined) {
            return;
          }

          if (uploadInfo.uploadType === 'single') {
            uploads.push({ id, file });
          } else if (uploadInfo.status !== 'done') {
            uploads.push({ id, file, uploadId: uploadInfo.uploadId, finishedParts: uploadInfo.finishedParts });
          }
        });

        tracksToRestart.forEach(({ uploadInfo, file, id }) => {
          if (file === undefined) {
            return;
          }

          if (uploadInfo.uploadType === 'single') {
            uploads.push({ id, file });
          } else if (uploadInfo.status !== 'done') {
            uploads.push({ id, file, uploadId: uploadInfo.uploadId, finishedParts: uploadInfo.finishedParts });
          }
        });

        if (trackPreview) {
          const { id, file, uploadInfo } = trackPreview;
          if (uploadInfo.uploadType === 'single') {
            uploads.push({ id, file });
          } else if (uploadInfo.status !== 'done') {
            uploads.push({ id, file, uploadId: uploadInfo.uploadId, finishedParts: uploadInfo.finishedParts });
          }
        }

        return UploadActions.start({ sessionId, uploads });
      }),
    );
  },
  { functional: true },
);

export const startFileUpload = createEffect(
  () => {
    const store = inject<WizardStore>(Store);
    const currentSessionId = store.selectSignal(valueSelectors.id);
    const uploadProgress = store.selectSignal(wizardFeature.selectUploadProgress);

    return inject(Actions).pipe(
      ofType(UploadActions.start),
      delayWhen(({ backoff, attempt = 1 }) => {
        if (!backoff) {
          return of(0);
        }

        const minDuration = attempt * attempt * 500;
        const duration = minDuration + Math.random() * minDuration;

        return timer(duration);
      }),
      mergeMap(({ sessionId, uploads, attempt = 1 }) => {
        return uploads.map(
          (
            upload,
          ):
            | ({ type: 'multi' } & Parameters<typeof multiPartUpload>[0])
            | ({ type: 'single' } & Parameters<typeof singlePartUpload>[0]) =>
            upload.file.size > MAX_SIZE_SINGLE_PART_UPLOAD
              ? {
                  ...upload,
                  attempt,
                  type: 'multi',
                  sessionId,
                }
              : {
                  ...upload,
                  attempt,
                  type: 'single',
                  sessionId,
                },
        );
      }),
      mergeMap(
        (params) => {
          // Queued uploads may be outdated once a slot frees up (session switched or upload canceled).
          if (params.sessionId !== currentSessionId() || uploadProgress()[params.id] === undefined) {
            return EMPTY;
          }

          return params.type === 'single' ? singlePartUpload(params) : multiPartUpload(params);
        },
        // Only 3 at a time. We avoid loading many files at the same time and there is a small limit on parallel uploads anyways.
        3,
      ),
    );
  },
  { functional: true },
);

export const cancelFileUpload = createEffect(
  () => {
    return inject(Actions).pipe(
      ofType(UploadActions.cancel),
      tap(({ id, fileUri, uploadId }) => {
        cancelUpload(id, fileUri, uploadId);
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const interruptFileUpload = createEffect(
  () => {
    return inject<WizardStore>(Store)
      .select(valueSelectors.id)
      .pipe(
        distinctUntilChanged(),
        tap(() => {
          interruptAll();
        }),
      );
  },
  { functional: true, dispatch: false },
);

export const analyzeFilesEffect = createEffect(
  () => {
    const store = inject<WizardStore>(Store);
    const http = inject(HttpClient);

    return store.select(trackToAnalyze).pipe(
      filter(isDefined),
      exhaustMap(({ uploadInfo: { objectUri }, id }) => {
        return http
          .post('/api/v5/audio/tracks/analyze-import-file', { file_uri: objectUri }, { context: skipHttpErrorPage() })
          .pipe(
            finalize(() => {
              // Using finalize to ensure post is complete before next tracklisting update fires.
              store.dispatch(AsyncValidationActions.trackWasAnalyzed({ id }));
            }),
          );
      }),
    );
  },
  { functional: true, dispatch: false },
);

const existingTracksDependencies = createSelector({
  tracks: valueSelectors.tracklisting,
  existingTrackData: wizardFeature.selectExistingTrackData,
});
export const loadExistingTracksEffect = createEffect(
  () => {
    const store = inject<WizardStore>(Store);
    const http = inject(HttpClient);

    return store.select(existingTracksDependencies).pipe(
      map(({ tracks, existingTrackData }) => {
        const ids: number[] = [];

        tracks.forEach((t) => {
          if (t.existing && !(t.id in existingTrackData)) {
            ids.push(t.id);
          }
        });

        return ids;
      }),
      filter((list) => list.length > 0),
      mergeMap((ids) => {
        return from(ids).pipe(
          mergeMap((id) =>
            http.get(`/api/v5/audio/tracks/${id}`, { context: skipHttpErrorPage() }).pipe(
              map((result) => {
                if (
                  !conformsTo({
                    isrc: isNullable(isString),
                    script_locale: isNullable(isString),
                    audio_locale: isNullable(isString),
                    title: isNullable(isString),
                    title_version: isNullable(isString),
                    p_line: isNullable(isString),
                    publishers: isArrayAndEveryElement(isString),
                    is_remastered_version: isNullable(isBoolean),
                    remastered_year: isNullable(isNumber),
                    is_cover_version: isNullable(isBoolean),
                    is_live_recording: isNullable(isBoolean),
                    is_instrumental: isNullable(isBoolean),
                    is_a_capella: isNullable(isBoolean),
                    is_public_domain: isNullable(isBoolean),
                    lyrics: isNullable(isString),
                    content_rating: isNullable(isExplicitness),
                    preview_snippet_offset: isNullable(isNumber),
                    preview_snippet_duration: isNullable(isNumber),
                    ai_usage: isNullable(
                      conformsTo({
                        ai_level_audio: isNullable(isKIPolicy),
                        ai_level_video: isNullable(isKIPolicy),
                        ai_level_lyrics: isNullable(isKIPolicy),
                        ai_level_composition: isNullable(isKIPolicy),
                      }),
                    ),
                    display_artist: isNullable(isString),
                    contributors: isArrayAndEveryElement(
                      conformsTo({
                        contributor_id: isNumber,
                        name: isString,
                        role_ids: isArrayAndEveryElement(isNumber),
                      }),
                    ),
                    genre1: isNullable(
                      conformsTo({
                        genre_id: isNumber,
                        name: isString,
                      }),
                    ),
                    genre2: isNullable(
                      conformsTo({
                        genre_id: isNumber,
                        name: isString,
                        genre_set: isString,
                      }),
                    ),
                    genre3: isNullable(
                      conformsTo({
                        genre_id: isNumber,
                        name: isString,
                        genre_set: isString,
                      }),
                    ),
                  })(result)
                ) {
                  throw new Error(`Received invalid result loading existing track: ${JSON.stringify(result)}`);
                }

                const genre: (Genre | undefined)[] = [undefined];
                const bisacGenre: (Genre | undefined)[] = [undefined];
                const wgsGenre: (Genre | undefined)[] = [undefined];

                if (result.genre1) {
                  genre[0] = {
                    id: String(result.genre1.genre_id),
                    name: result.genre1.name,
                  };
                }

                if (result.genre2) {
                  if (result.genre2.genre_set === 'DMB') {
                    genre[1] = {
                      id: String(result.genre2.genre_id),
                      name: result.genre2.name,
                    };
                  } else {
                    bisacGenre[0] = {
                      id: String(result.genre2.genre_id),
                      name: result.genre2.name,
                    };
                  }
                }

                if (result.genre3) {
                  if (result.genre3.genre_set === 'DMB') {
                    genre[2] = {
                      id: String(result.genre3.genre_id),
                      name: result.genre3.name,
                    };
                  } else {
                    wgsGenre[0] = {
                      id: String(result.genre3.genre_id),
                      name: result.genre3.name,
                    };
                  }
                }

                let pYear = '';
                let pText = '';
                if (result.p_line) {
                  pYear = result.p_line.substring(0, 4);
                  pText = result.p_line.substring(5);
                }

                let aiUsageToggle = false;
                let aiUsageLyrics: KIPolicies | '' = '';
                let aiUsageAudio: KIPolicies | '' = '';
                let aiUsageComposition: KIPolicies | '' = '';
                let aiUsageVideo: KIPolicies | '' = '';
                if (result.ai_usage) {
                  if (result.ai_usage.ai_level_lyrics) {
                    aiUsageLyrics = result.ai_usage.ai_level_lyrics;
                  }
                  if (result.ai_usage.ai_level_audio) {
                    aiUsageAudio = result.ai_usage.ai_level_audio;
                  }
                  if (result.ai_usage.ai_level_composition) {
                    aiUsageComposition = result.ai_usage.ai_level_composition;
                  }
                  if (result.ai_usage.ai_level_video) {
                    aiUsageVideo = result.ai_usage.ai_level_video;
                  }

                  if (
                    (aiUsageLyrics !== '' && aiUsageLyrics !== KIPolicies.NONE) ||
                    (aiUsageAudio !== '' && aiUsageAudio !== KIPolicies.NONE) ||
                    (aiUsageComposition !== '' && aiUsageComposition !== KIPolicies.NONE) ||
                    (aiUsageVideo !== '' && aiUsageVideo !== KIPolicies.NONE)
                  ) {
                    aiUsageToggle = true;
                  }
                }

                return WizardSessionActions.finishedLoadingExistingTrack({
                  id,
                  metadata: {
                    genre,
                    bisacGenre,
                    wgsGenre,
                    titleLanguage: result.audio_locale ?? '',
                    lyricsLanguage: result.script_locale ?? '',
                    explicit: result.content_rating ?? Explicitness.NON_EXPLICIT,
                    pYear,
                    pText,
                    publisher: result.publishers,
                    instrumental: result.is_instrumental ?? false,
                    live: result.is_live_recording ?? false,
                    publicDomain: result.is_public_domain ?? false,
                    coverVersion: result.is_cover_version ?? false,
                    acapella: result.is_a_capella ?? false,
                    remastered: result.is_remastered_version ?? false,
                    remasteredYear: typeof result.remastered_year === 'number' ? String(result.remastered_year) : '',
                    noSpeaker: false,
                    snippetStart:
                      typeof result.preview_snippet_offset === 'number' ? String(result.preview_snippet_offset) : '',
                    snippetDuration:
                      typeof result.preview_snippet_duration === 'number'
                        ? String(result.preview_snippet_duration)
                        : '',
                    lyrics: result.lyrics ?? '',
                    aiUsageToggle,
                    aiUsageLyrics,
                    aiUsageAudio,
                    aiUsageComposition,
                    aiUsageVideo,
                  },
                  contributors: result.contributors.map((c) => {
                    const id = String(c.contributor_id);
                    return {
                      id,
                      roles: c.role_ids.map(String),
                      contributor: {
                        id,
                        name: c.name,
                        clientId: '', // Will not be used for existing tracks
                        appleArtistId: undefined,
                        spotifyArtistId: undefined,
                        birthday: undefined,
                      },
                    };
                  }),
                  title: result.title ?? undefined,
                  titleVersion: result.title_version ?? undefined,
                  isrc: result.isrc ?? undefined,
                  displayArtist: result.display_artist ?? undefined,
                });
              }),
              catchError((err) => {
                logError(err);

                return of(WizardSessionActions.failedLoadingExistingTrack({ id }));
              }),
            ),
          ),
          startWith(WizardSessionActions.startedLoadingExistingTracks({ ids })),
        );
      }),
    );
  },
  { functional: true },
);
